class OnlineService {
  constructor() {
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.lastSuccessfulNetworkAt = this._readTimestamp(
      'dij.lastSuccessfulNetworkAt'
    );
    this.lastNetworkFailureAt = this._readTimestamp('dij.lastNetworkFailureAt');
    this.passiveSignalTtlMs = 20000;

    this._internetProbe = {
      lastAt: 0,
      promise: null,
      result: null,
      cooldownMs: 2000,
      timeoutMs: 2500,
      url:
        process.env.VUE_APP_CONNECTIVITY_CHECK_URL ||
        'https://clients3.google.com/generate_204',
      fetchOptions: { method: 'GET', mode: 'no-cors' },
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.isOnline = true;
        this._internetProbe.result = null;
      });
      window.addEventListener('offline', () => {
        this.isOnline = false;
        this.recordNetworkFailure();
        this._internetProbe.result = null;
      });
    }
  }

  _readTimestamp(key) {
    if (typeof window === 'undefined') return 0;
    try {
      const raw = window.sessionStorage.getItem(key);
      const value = Number(raw);
      return Number.isFinite(value) ? value : 0;
    } catch (e) {
      return 0;
    }
  }

  _writeTimestamp(key, value) {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(key, String(value || 0));
    } catch (e) {
      // ignore storage failures
    }
  }

  /**
   * Generic probe: handles cooldown, in-flight deduplication, timeout and fetch.
   */
  _probe(state, { force = false } = {}) {
    const now = Date.now();

    if (state.promise) {
      return state.promise;
    }

    if (!force && now - state.lastAt < state.cooldownMs) {
      return state.result !== null ? state.result : this.isOnline;
    }

    state.lastAt = now;
    state.promise = new Promise((resolve) => {
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), state.timeoutMs);

      fetch(state.url, {
        ...state.fetchOptions,
        cache: 'no-store',
        signal: controller.signal,
      })
        .then(() => {
          state.result = true;
          resolve(true);
        })
        .catch(() => {
          state.result = false;
          resolve(false);
        })
        .finally(() => {
          clearTimeout(tid);
          state.promise = null;
        });
    });

    return state.promise;
  }

  async probeConnectivity() {
    return this.getStatus();
  }

  async probeInternetConnectivity(force = false) {
    return this._probe(this._internetProbe, { force });
  }

  recordSuccessfulNetworkActivity() {
    const now = Date.now();
    this.lastSuccessfulNetworkAt = now;
    this.lastNetworkFailureAt = 0;
    this.isOnline = true;
    this._writeTimestamp('dij.lastSuccessfulNetworkAt', now);
    this._writeTimestamp('dij.lastNetworkFailureAt', 0);
  }

  recordNetworkFailure() {
    const now = Date.now();
    this.lastNetworkFailureAt = now;
    this.isOnline = false;
    this._writeTimestamp('dij.lastNetworkFailureAt', now);
  }

  async getStatus() {
    const now = Date.now();

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.isOnline = false;
      return false;
    }

    // Browser reports online but last known network signal was a recent failure.
    // Re-validate using internet probe so we can recover immediately when connection is restored.
    if (
      this.lastNetworkFailureAt > this.lastSuccessfulNetworkAt &&
      now - this.lastNetworkFailureAt < this.passiveSignalTtlMs
    ) {
      const canReachInternet = await this.probeInternetConnectivity(true);
      if (canReachInternet) {
        this.recordSuccessfulNetworkActivity();
        return true;
      }

      this.recordNetworkFailure();
      return false;
    }

    if (now - this.lastSuccessfulNetworkAt < this.passiveSignalTtlMs) {
      this.isOnline = true;
      return true;
    }

    if (typeof navigator !== 'undefined') {
      this.isOnline = navigator.onLine;
      return navigator.onLine;
    }

    return this.isOnline;
  }

  async canConnectRealtime() {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.isOnline = false;
      return false;
    }
    return this.probeInternetConnectivity();
  }
}

export default new OnlineService();
